import { computed, ref, watch } from 'vue'
import { createSharedComposable } from '@vueuse/core'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { v4 as uuid } from 'uuid'
import { isEqual } from 'lodash-es'

import { constructFilterOptions, type DeconstructedOption } from '@/modules/conversations/utils/filters'
import { getTicketsPaginated } from '@/modules/conversations/api/tickets'
import { CONVERSATIONS_LIST_LIMIT, CONVERSATIONS_LIST_FULL_LIMIT } from '@/modules/settings/constants/conversations'
import { session } from '@/composables/useSession'

import { mapFilterOptions } from '../components/ai/utils'

import usePromptTestingWorkspace from './usePromptTestingWorkspace'

export default createSharedComposable(() => {
  const { activeWorkspace } = usePromptTestingWorkspace()

  const isInitialPromptTestingView = ref(true)
  const promptTestingConversationsFilterOptions = ref<DeconstructedOption[]>([])
  const ticketCount = ref(0)
  let currentPage = 0

  const promptConversationsRatingsMap = ref<Record<string, number>>({})

  watch(
    () => promptTestingConversationsFilterOptions.value,
    () => {
      currentPage = 0
    },
  )

  const {
    data: conversationsData,
    isPending,
    fetchNextPage: loadMoreConversations,
    refetch: refetchConversations,
  } = useInfiniteQuery({
    queryKey: ['conversations-list', promptTestingConversationsFilterOptions],
    queryFn: async ({ pageParam }) => {
      currentPage = pageParam
      const filterOptions = promptTestingConversationsFilterOptions.value
      if (!filterOptions.length) return

      const options = constructFilterOptions(
        filterOptions.filter(
          (o) => !o.option.isAutoQa && !o.option.isSpotlight && !o.option.isCustomCategory && !o.option.isIntelligent,
        ),
      )
      const autoQaOptions = constructFilterOptions(filterOptions.filter((o) => o.option.isAutoQa))
      const spotlightOptions = constructFilterOptions(filterOptions.filter((o) => o.option.isSpotlight))
      const customCategoryOptions = constructFilterOptions(filterOptions.filter((o) => o.option.isCustomCategory))
      const intelligentFilterOptions = constructFilterOptions(filterOptions.filter((o) => o.option.isIntelligent))

      return await getTicketsPaginated(
        {
          limit: CONVERSATIONS_LIST_LIMIT,
          offset: pageParam * CONVERSATIONS_LIST_LIMIT,
          sort: 'createdAt',
          includeAgents: true,
        },
        {
          isPublic: true,
          name: 'temporary-name',
          options,
          autoQaOptions,
          spotlightOptions,
          customCategoryOptions,
          intelligentFilterOptions,
        },
        `payments/${session.account.id}/teams/${activeWorkspace.value.id}`,
      )
    },
    initialPageParam: currentPage,
    maxPages: 10,
    getNextPageParam: (lastPage, pages) =>
      lastPage?.data.length < CONVERSATIONS_LIST_LIMIT ? undefined : pages.length,
    select: (data) => {
      const pages = data.pages.flatMap((page) => page.data)

      return {
        data: pages.map((conversation) => ({
          id: conversation.externalId,
          title: conversation.subject,
          assigneeId: conversation.assigneeId,
          assigneeName: conversation.assigneeName || '',
          assigneePictureUrl: conversation.assigneePictureUrl || '',
          connectionId: conversation.paymentTokenId.toString(),
          agents: conversation.agents || [],
        })),
        total: pages.length,
        pageParams: data.pageParams,
      }
    },
  })

  const hasManualReviews = computed(() => Object.keys(promptConversationsRatingsMap.value).length)
  const nextConversationsBatchCount = computed(() => {
    const conversationsLeftToFetch =
      Math.min(ticketCount.value || 0, CONVERSATIONS_LIST_FULL_LIMIT) - (conversationsData.value?.total || 0)
    return Math.min(CONVERSATIONS_LIST_LIMIT, conversationsLeftToFetch)
  })

  const isConversationsLimitReached = computed(() => {
    return conversationsData.value?.data.length >= CONVERSATIONS_LIST_FULL_LIMIT
  })

  const clearConversationRatings = () => {
    promptConversationsRatingsMap.value = {}
  }
  const hasOnlyPredefinedFilters = computed(() => {
    const mappedNewFilterOptions = mapFilterOptions(newFilterOptions.value)
    const mappedCurrentFilterOptions = mapFilterOptions(promptTestingConversationsFilterOptions.value)
    return !mappedCurrentFilterOptions.length || isEqual(mappedNewFilterOptions, mappedCurrentFilterOptions)
  })

  const removeInvalidManualReviews = () => {
    if (!hasManualReviews.value) return

    const conversationIds = new Set(conversationsData.value.data?.map((item) => item.id) ?? [])

    Object.keys(promptConversationsRatingsMap.value).forEach((fullId: string) => {
      const conversationId = fullId.split(':')[0]

      if (!conversationIds.has(conversationId)) delete promptConversationsRatingsMap.value[fullId]
    })
  }

  const resetFiltersSelected = () => {
    promptTestingConversationsFilterOptions.value = []
  }

  const newFilterOptions = ref([
    {
      id: uuid(),
      option: {
        name: 'TICKET_CREATED_DATE',
        label: 'filter.option.label.ticket_created_date',
      },
      type: 'DEFAULT',
      condition: {
        name: 'DATE_LAST_7_DAYS',
        label: 'filter.condition.label.date_last_7_days',
        valueRequired: false,
      },
      values: [],
    },
    {
      id: uuid(),
      option: {
        name: 'TICKET_STATUS',
        label: 'filter.option.label.ticket_status',
      },
      type: 'DEFAULT',
      condition: {
        name: 'STRING_LIST_EQUALS',
        label: 'filter.condition.label.string_list_equals',
        valueRequired: true,
      },
      values: [
        {
          value: 'solved',
          sourceType: [null],
          label: 'Solved',
        },
        {
          value: 'closed',
          sourceType: [null],
          label: 'Closed',
        },
      ],
    },
    {
      id: uuid(),
      option: {
        name: 'TICKET_REPLY_COUNT',
        label: 'filter.option.label.ticket_reply_count',
      },
      type: 'DEFAULT',
      condition: {
        name: 'INTEGER_HIGHER',
        label: 'filter.condition.label.integer_higher',
        valueRequired: true,
      },
      values: [
        {
          value: '4',
          label: null,
        },
      ],
    },
  ])

  const toggledParticipantItems = ref<Record<string, boolean>>({})

  const submittingFilters = ref(false)
  const isLoading = computed(() => isPending.value || submittingFilters.value)

  return {
    isLoading,
    submittingFilters,
    conversationsData,
    loadMoreConversations,
    promptTestingConversationsFilterOptions,
    isConversationsLimitReached,
    nextConversationsBatchCount,
    ticketCount,
    refetchConversations,
    promptConversationsRatingsMap,
    hasManualReviews,
    clearConversationRatings,
    isInitialPromptTestingView,
    newFilterOptions,
    hasOnlyPredefinedFilters,
    toggledParticipantItems,
    resetFiltersSelected,
    removeInvalidManualReviews,
  }
})
